import { useEffect, useState, useRef } from 'react';

interface HeroLoadingState {
  textContentLoaded: boolean;
  threeJSLoaded: boolean;
  animationsReady: boolean;
  isFullyLoaded: boolean;
}

export const useHeroLoader = () => {
  const [loadingState, setLoadingState] = useState<HeroLoadingState>({
    textContentLoaded: false,
    threeJSLoaded: false,
    animationsReady: false,
    isFullyLoaded: false
  });

  const textContentRef = useRef<HTMLDivElement>(null);
  const threeJSRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if text content is loaded (DOM elements are ready)
    const checkTextContent = () => {
      if (textContentRef.current) {
        const title = textContentRef.current.querySelector('.hero-title');
        const subtitle = textContentRef.current.querySelector('.hero-subtitle');
        const features = textContentRef.current.querySelector('.hero-features');
        
        if (title && subtitle && features) {
          setLoadingState(prev => ({
            ...prev,
            textContentLoaded: true
          }));
        }
      }
    };

    // Check if Three.js content is loaded
    const checkThreeJS = () => {
      if (threeJSRef.current) {
        const canvas = threeJSRef.current.querySelector('canvas');
        if (canvas) {
          // Wait for canvas to be fully rendered
          const checkCanvasReady = () => {
            if (canvas.width > 0 && canvas.height > 0) {
              setLoadingState(prev => ({
                ...prev,
                threeJSLoaded: true
              }));
            } else {
              setTimeout(checkCanvasReady, 100);
            }
          };
          checkCanvasReady();
        }
      }
    };

    // Check animations readiness
    const checkAnimations = () => {
      // Wait a bit for CSS animations to initialize
      setTimeout(() => {
        setLoadingState(prev => ({
          ...prev,
          animationsReady: true
        }));
      }, 500);
    };

    // Run checks
    checkTextContent();
    checkThreeJS();
    checkAnimations();

    // Re-check periodically until all are loaded
    const interval = setInterval(() => {
      if (!loadingState.textContentLoaded) checkTextContent();
      if (!loadingState.threeJSLoaded) checkThreeJS();
    }, 100);

    return () => clearInterval(interval);
  }, [loadingState.textContentLoaded, loadingState.threeJSLoaded]);

  // Update isFullyLoaded when all components are ready
  useEffect(() => {
    const isFullyLoaded = loadingState.textContentLoaded && 
                         loadingState.threeJSLoaded && 
                         loadingState.animationsReady;

    setLoadingState(prev => ({
      ...prev,
      isFullyLoaded
    }));
  }, [loadingState.textContentLoaded, loadingState.threeJSLoaded, loadingState.animationsReady]);

  return {
    loadingState,
    textContentRef,
    threeJSRef,
    isHeroLoaded: loadingState.isFullyLoaded
  };
};
